import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LiveLayout from "./LiveLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { User, Building2, Users, Star, Shield, ChevronRight, LogOut, LayoutDashboard } from "lucide-react";

export default function LiveAccount() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [follows, setFollows] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/live/me/teams").then((r) => setData(r.data)).catch(() => setData({ organizations: [], teams: [] }));
    api.get("/live/me/follows").then((r) => setFollows(r.data)).catch(() => {});
  }, [user]);

  return (
    <LiveLayout>
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-5 py-7 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#E63946] grid place-items-center font-display text-2xl shrink-0">
            {user?.name?.[0]?.toUpperCase() || <User className="w-7 h-7" />}
          </div>
          <div className="min-w-0">
            <div className="label-overline text-[#E63946]">CBR Live Account</div>
            <h1 className="font-display text-2xl uppercase tracking-tight truncate">{user?.name || "Guest"}</h1>
            {user && <div className="text-xs text-white/50 capitalize">{user.role?.replace("_", " ")}</div>}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-6 space-y-6" data-testid="live-account">
        {!user ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <User className="w-10 h-10 mx-auto text-slate-300" />
            <p className="text-sm text-slate-500 mt-3">Sign in to manage teams, organizations and follows.</p>
            <Link to="/login" className="inline-block mt-4 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-5 py-2 rounded-sm">Sign In</Link>
          </div>
        ) : <>
          <Block icon={Building2} title={`My Organizations (${data?.organizations?.length || 0})`}>
            {!data ? <Loading /> : data.organizations.length === 0
              ? <Empty>No organizations yet. Use the <b>+</b> button to create one.</Empty>
              : data.organizations.map((o) => (
                <Row key={o.id} testid={`account-org-${o.id}`} title={o.name} sub={[o.type, o.province].filter(Boolean).join(" · ")} icon={Building2} />
              ))}
          </Block>

          <Block icon={Users} title={`My Teams (${data?.teams?.length || 0})`}>
            {!data ? <Loading /> : data.teams.length === 0
              ? <Empty>No teams yet. Create one from the <b>+</b> menu.</Empty>
              : data.teams.map((t) => (
                <Row key={t.id} testid={`account-team-${t.id}`} onClick={() => nav(`/live/teams/${t.slug}`)}
                  title={t.name} sub={[t.age_division, t.province].filter(Boolean).join(" · ")} logo={t.logo_url} chevron />
              ))}
          </Block>

          <Block icon={Star} title={`Teams I Follow (${follows.length})`}>
            {follows.length === 0 ? <Empty>You're not following any teams yet.</Empty>
              : follows.map((t) => (
                <Row key={t.id} testid={`account-follow-${t.id}`} onClick={() => nav(`/live/teams/${t.slug}`)}
                  title={t.name} sub={[t.age_division, t.province].filter(Boolean).join(" · ")} logo={t.logo_url} chevron />
              ))}
          </Block>

          <div className="flex flex-col gap-2">
            <Link to="/dashboard" data-testid="account-dashboard" className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm font-semibold text-[#0A192F] hover:border-[#E63946]">
              <LayoutDashboard className="w-4 h-4" /> Main Dashboard
            </Link>
            <button data-testid="account-logout" onClick={async () => { await logout(); nav("/live"); }}
              className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm font-semibold text-[#E63946] hover:border-[#E63946]">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </>}
      </div>
    </LiveLayout>
  );
}

function Block({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="label-overline flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-[#E63946]" />{title}</div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
function Row({ title, sub, logo, icon: Icon, chevron, onClick, testid }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp data-testid={testid} onClick={onClick} className={`w-full flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3 text-left ${onClick ? "hover:border-[#E63946]" : ""}`}>
      <div className="w-9 h-9 rounded-sm bg-slate-100 overflow-hidden grid place-items-center shrink-0">
        {logo ? <img src={logo} alt="" className="w-full h-full object-cover" /> : Icon ? <Icon className="w-4 h-4 text-slate-400" /> : <Shield className="w-4 h-4 text-slate-300" />}
      </div>
      <div className="flex-1 min-w-0"><div className="text-sm font-semibold text-[#0A192F] truncate">{title}</div>{sub && <div className="text-[11px] text-slate-400 capitalize">{sub}</div>}</div>
      {chevron && <ChevronRight className="w-4 h-4 text-slate-300" />}
    </Comp>
  );
}
const Empty = ({ children }) => <div className="text-sm text-slate-400 py-2">{children}</div>;
const Loading = () => <div className="text-sm text-slate-400 py-2">Loading…</div>;
